import React, { useState } from 'react'
import API from '../../../lib/api'
import { getMessage } from '../../../lib/translator'
import PopupModal from '../../../components/Popup/Modal'
import Textarea from '../../../components/Form/Inputs/Textarea'
import './AddDomainsModal.css'

const API_URL = '/lms/content/domains'

const API_ERROR_MESSAGE_MAP = {
  'domain must be a valid url': 'Please enter a valid domain name',
  'domain field is required and must be a string':
    'Please enter a valid domain name',
  'domain already exists': 'Domain already exist',
}

function mapApiErrorToUserMessage(message) {
  if (!message || typeof message !== 'string') {
    return message
  }
  const original = message
  const normalized = message.trim().toLowerCase().replace(/\.+$/, '')
  const withoutValidationPrefix = normalized
    .replace(/^validation error:\s*/, '')
    .trim()
  return (
    API_ERROR_MESSAGE_MAP[normalized] ??
    API_ERROR_MESSAGE_MAP[withoutValidationPrefix] ??
    original
  )
}

/** Map known API phrases when showing `error` (handles optional ` (domain)` suffix). */
function mapDisplayError(displayed) {
  if (!displayed || typeof displayed !== 'string') {
    return displayed
  }
  const trimmed = displayed.trim()
  const withSuffix = /^(.+) \(([^)]+)\)$/.exec(trimmed)
  const body = withSuffix ? withSuffix[1].trim() : trimmed
  const suffix = withSuffix ? ` (${withSuffix[2]})` : ''
  const mapped = mapApiErrorToUserMessage(body)
  return mapped !== body ? `${mapped}${suffix}` : displayed
}

const DEFAULT_END_TIME = '2999-10-01T00:00:00+08:00'

function getDefaultStartTime() {
  return new Date().toISOString()
}

function parseDomains(value) {
  if (!value || typeof value !== 'string') {
    return []
  }
  return value
    .split(/[\n,]+/)
    .map((d) => d.trim())
    .filter(Boolean)
}

function normalizeDomain(domain) {
  return String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
}

function buildPayload(domain) {
  const url =
    domain.startsWith('http://') || domain.startsWith('https://')
      ? domain
      : `https://${domain}`
  return {
    contentType: 'domain',
    key: 'whitelisting',
    data: {
      startTime: getDefaultStartTime(),
      endTime: DEFAULT_END_TIME,
      isDisabled: false,
      domain: url,
    },
  }
}

export default function AddDomainsModal({ show, onClose, onSuccess }) {
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const handleClose = () => {
    setValue('')
    setError(null)
    onClose()
  }

  const handleSubmit = () => {
    const domains = parseDomains(value)
    if (domains.length === 0) {
      setError(getMessage('whitelistDomain.addDomains.error.empty'))
      return
    }

    const normalizedInput = domains.map(normalizeDomain)
    const duplicateInInput = normalizedInput.find(
      (domain, index) => normalizedInput.indexOf(domain) !== index
    )
    if (duplicateInInput) {
      setError(`Domain already added in input: ${duplicateInInput}`)
      return
    }

    setError(null)
    setSubmitting(true)
    const api = new API({ url: API_URL })
    const promises = domains.map((domain) =>
      api.post(buildPayload(normalizeDomain(domain))).catch((err) => {
        const e = new Error(err?.message || 'Failed to add domain')
        e.domain = domain
        e.cause = err
        throw e
      })
    )
    Promise.all(promises)
      .then(() => {
        setSubmitting(false)
        handleClose()
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((failed) => {
        setSubmitting(false)
        const fallbackMessage = getMessage(
          'whitelistDomain.addDomains.error.generic'
        )
        const failedDomain = failed?.domain
        const failedError = failed?.cause ?? failed
        const rawMessage = failedError?.message
        const userMessage = rawMessage
          ? mapApiErrorToUserMessage(rawMessage)
          : null
        setError(
          userMessage
            ? `${userMessage}${failedDomain ? ` (${failedDomain})` : ''}`
            : failedDomain
              ? `Failed to add domain: ${failedDomain}`
              : fallbackMessage
        )
      })
  }

  if (!show) {
    return null
  }

  return (
    <PopupModal show={show} close={handleClose}>
      <div className="add-domains-modal dialogPopup">
        <div className="warn">
          <div className="add-domains-modal-title">
            {getMessage('whitelistDomain.addDomains.title')}
          </div>
          <p className="add-domains-modal-description">
            {getMessage('whitelistDomain.addDomains.description')}
          </p>
          <div className="add-domains-modal-field">
            <label htmlFor="whitelist-domain-textarea" className="label">
              {getMessage('whitelistDomain.addDomains.label')}
            </label>
            <Textarea
              id="whitelist-domain-textarea"
              name="domains"
              placeholder={getMessage('whitelistDomain.addDomains.placeholder')}
              value={value}
              onChange={setValue}
              className="add-domains-textarea"
            />
          </div>
          {error && (
            <p className="add-domains-modal-error">{mapDisplayError(error)}</p>
          )}
          <div className="add-domains-modal-actions">
            <button
              type="button"
              className="button"
              data-testid="add-domains-cancel"
              onClick={handleClose}
            >
              {getMessage('whitelistDomain.addDomains.cancel')}
            </button>
            <button
              type="button"
              className="primary button"
              data-testid="add-domains-submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? getMessage('whitelistDomain.addDomains.submitting')
                : getMessage('whitelistDomain.addDomains.submit')}
            </button>
          </div>
        </div>
      </div>
    </PopupModal>
  )
}
